// Theft & roadside to buy: the row words, the two field checks and the three-step save of a vendor
// policy (contract 15 Oct 2026). There is no insurer API, so ops buy one policy per Assure+
// subscription from the vendor by hand and record it here; the warranty service then marks the
// covers active and sends the PDF to the rider.
import CheckValid from "../../Common/CheckValid";
import { formatDate, safeHttpUrl } from "../common/format";
import { billingModeLabel } from "../common/opsLabels";
import { newClientRequestId, requestIdFor, settleRequestId } from "../common/clientRequest";

export const PAGE_SIZE = 50;
// The contract's "10 MB", read as MiB, the same as the OMS check.
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

const checked = (value, states) => {
  let message = "";
  CheckValid(value, {
    ...states,
    error: (m) => {
      message = m;
    },
  });
  return message;
};

// "" when fine; the rules live in CheckValid.
export const policyNumberMessage = (text) => checked(text, { type: "warranty_vendor_policy_number" });
export const pdfFileMessage = (file) => checked(file, { type: "warranty_vendor_policy_pdf", max: MAX_PDF_BYTES });

const joined = (...parts) =>
  parts.filter((part) => part !== null && part !== undefined && part !== "").join(" · ");

export const planLabel = (item) =>
  joined(item.displayName || item.tier, item.billingMode ? billingModeLabel(item.billingMode).toLowerCase() : "");

export const coverWindow = (item) =>
  item.validFrom || item.validTo ? `${formatDate(item.validFrom)} to ${formatDate(item.validTo)}` : "-";

export const customerLabel = (item) => joined(item.customerFirstName, item.maskedPhone) || "-";

export const daysWaitingLabel = (days) => {
  if (!Number.isInteger(days) || days < 0) return "-";
  if (days === 0) return "Today";
  return `${days} day${days === 1 ? "" : "s"}`;
};

// Why a save stopped. ALREADY_ISSUED takes the row off the list; NOT_ACCEPTED and UPLOAD start the
// next save from a fresh upload; NETWORK and OTHER keep the upload, so Save again only repeats the
// record, with the same request id when the answer may have been lost.
export const SAVE_FAILURE = {
  LINK: "link",
  UPLOAD: "upload",
  NOT_ACCEPTED: "not_accepted",
  ALREADY_ISSUED: "already_issued",
  NETWORK: "network",
  OTHER: "other",
};

const ALREADY_ISSUED_CODE = "VENDOR_POLICY_ALREADY_ISSUED";
const MESSAGES = {
  link: "The upload link from the warranty service was not usable. Try again, and tell engineering if it keeps happening.",
  upload: "The PDF could not be uploaded. Check your connection and save again.",
  linkRefused: "The warranty service did not accept this file. It must be a PDF of at most 10 MB.",
  recordRefused:
    "The warranty service did not accept the file: it is not a PDF, or its upload did not finish. Choose the PDF again and save.",
  alreadyIssued: "A vendor policy was already added for this subscription, so this one was not saved.",
};

const isRefusedFile = (result) => result.httpStatus === 400 || result.httpStatus === 422;

const failure = (kind, message, uploaded = null) => ({ ok: false, kind, message, uploaded });

// One answer that is not a success, as a failure of the save. `step` is "link" or "record".
const failureOf = (result, step, uploaded) => {
  if (result.code === ALREADY_ISSUED_CODE) {
    return failure(SAVE_FAILURE.ALREADY_ISSUED, MESSAGES.alreadyIssued);
  }
  if (isRefusedFile(result)) {
    return failure(SAVE_FAILURE.NOT_ACCEPTED, step === "link" ? MESSAGES.linkRefused : MESSAGES.recordRefused);
  }
  const lost = result.httpStatus === undefined || result.httpStatus === null;
  return failure(lost ? SAVE_FAILURE.NETWORK : SAVE_FAILURE.OTHER, result.message, uploaded);
};

const httpsUrl = (value) => {
  const url = safeHttpUrl(value);
  return url && url.startsWith("https:") ? url : null;
};

// The save: (1) ask for an upload link, (2) PUT the PDF there, (3) record the policy number with
// the upload key. `uploaded` ({ file, uploadKey }) is a finished upload of this same file from an
// earlier save that stopped at step 3, so a retry skips steps 1 and 2. `requestRef` holds the
// record's client request id across retries (clientRequest.requestIdFor). `api` is
// { createUploadUrl, uploadPdf, recordPolicy } from warrantyApi.
export const saveVendorPolicy = async (api, { token, subscriptionId, policyNumber, file, uploaded, requestRef }) => {
  let upload = uploaded && uploaded.file === file ? uploaded : null;
  if (!upload) {
    // A new link for every upload: a link is only good for five minutes, so an old one is never replayed.
    const link = await api.createUploadUrl(token, subscriptionId, file.size, newClientRequestId());
    if (!link.ok) {
      return failureOf(link, "link", null);
    }
    const uploadUrl = httpsUrl(link.data?.uploadUrl);
    const uploadKey = typeof link.data?.uploadKey === "string" ? link.data.uploadKey : "";
    if (!uploadUrl || uploadKey === "") {
      return failure(SAVE_FAILURE.LINK, MESSAGES.link);
    }
    const put = await api.uploadPdf(uploadUrl, file);
    if (!put.ok) {
      return failure(SAVE_FAILURE.UPLOAD, MESSAGES.upload);
    }
    upload = { file, uploadKey };
  }

  const number = policyNumber.trim();
  const requestId = requestIdFor(requestRef, `${subscriptionId}|${number}|${upload.uploadKey}`);
  const result = await api.recordPolicy(token, subscriptionId, number, upload.uploadKey, requestId);
  settleRequestId(requestRef, result);
  if (result.ok) {
    return { ok: true, data: result.data, uploaded: null };
  }
  return failureOf(result, "record", upload);
};
